/**
 * Core Ride to Rebuild charity domain types.
 */

import { IHouseholdLogin } from "./IHouseholdLogin";
import { IHouseholdViewer } from "./IHouseholdViewer";
import { IImportedRideOrigin } from "./IImportedRideOrigin";
import { LatestRide } from "./LatestRide";
import { LeaderboardHousehold } from "./LeaderboardHousehold";
import { MileageDisposition } from "./MileageDispositionEnum";

export { CharityQrTarget } from "./CharityQrTargetEnum";
export { CharityRoute } from "./CharityRouteEnum";
export { CharitySessionStatus } from "./CharitySessionStatusEnum";
export { HouseholdRole } from "./HouseholdRoleEnum";
export { LeaderboardBoard } from "./LeaderboardBoardEnum";
export { MileageDisposition } from "./MileageDispositionEnum";
export type { ICharityConfirmation } from "./ICharityConfirmation";
export type { ICharityQrCode } from "./ICharityQrCode";
export type { IHouseholdLogin } from "./IHouseholdLogin";
export type { IHouseholdViewer } from "./IHouseholdViewer";
export type { IImportCandidate } from "./IImportCandidate";
export type { IImportedRideOrigin } from "./IImportedRideOrigin";
export type { IImportOutcome } from "./IImportOutcome";
export type { IProviderActivity } from "./IProviderActivity";
export type { IRiderRideGroup } from "./IRiderRideGroup";
export type { ITrackerProviderStatus } from "./ITrackerProviderStatus";
export { ImportCandidateDisposition } from "./ImportCandidateDispositionEnum";
export { ImportModalStep } from "./ImportModalStepEnum";
export { TrackerConnectOutcome } from "./TrackerConnectOutcomeEnum";
export { TrackerProvider } from "./TrackerProviderEnum";
export { ReportStep } from "./ReportStepEnum";
export { SubmissionReviewState } from "./SubmissionReview";
export { SubmissionRejectionReason } from "./SubmissionRejectionReasonEnum";
export type { CampaignFooterLink } from "./CampaignFooterLink";
export type { CampaignHeadline } from "./CampaignHeadline";
export type { CampaignSectionAction } from "./CampaignSectionAction";
export type { CampaignStartStep } from "./CampaignStartStep";
export type { CampaignTrackerGeometry } from "./CampaignTrackerGeometry";
export type {
  LeaderboardAdultApiEntry,
  LeaderboardApiResponse,
  LeaderboardHouseholdApiEntry,
  LeaderboardMinorApiEntry,
} from "./LeaderboardApiTypes";
export type { LatestRide } from "./LatestRide";
export type { LatestRidesResponse } from "./LatestRidesApiTypes";
export type { LeaderboardBoardTab } from "./LeaderboardBoardTab";
export type { LeaderboardHousehold } from "./LeaderboardHousehold";
export type { SignedInCharitySession } from "./SignedInCharitySession";
export type { SubmissionFailureCopy } from "./SubmissionFailureCopy";
export type { SubmissionRefusal, SubmissionReviewOutcome } from "./SubmissionReview";

export enum RiderType {
  Adult = "adult",
  Minor = "minor",
}

/**
 * Lifecycle of one ride report, mirroring the statuses the API can store.
 *
 * Only Counted moves the campaign's total. A member with no counterpart in
 * the API tells a household about an outcome no ride of theirs can reach.
 */
export enum ReportStatus {
  Pending = "pending",
  Counted = "counted",
  Rejected = "rejected",
  Duplicate = "duplicate",
  Withdrawn = "withdrawn",
}

export enum CampaignPhase {
  PreLaunch = "pre_launch",
  Running = "running",
  CapReached = "cap_reached",
  Closed = "closed",
}

export interface CharityRider {
  id: string;
  householdId: string;
  displayName: string;
  riderType: RiderType;
  hidden: boolean;
  createdAtMs: number;
}

export interface CharityHousehold {
  id: string;
  headEmail: string;
  displayName: string;
  createdAtMs: number;
}

export interface HouseholdWithRiders {
  household: CharityHousehold;
  riders: CharityRider[];
}

export interface CharityRideReport {
  id: string;
  householdId: string;
  submissionGroupId: string;
  /**
   * The one rider this record is for. Mileage is per rider, so a family ride
   * stores one record each rather than one record listing them.
   */
  riderId: string;
  /** Denormalised by the API, so listing rides needs no rider lookup. */
  riderDisplayName: string;
  /**
   * Denormalised alongside the name because it prices the ride: adults earn
   * per mile, minors per ride. That is also what says whether a distance of
   * zero below is a real figure or a distance the ride was never asked for.
   */
  riderType: RiderType;
  /**
   * Miles as reported. Zero on a minor's ride means no distance was stated —
   * they earn per ride, so the form does not ask them for one.
   */
  miles: number;
  rideDateMs: number;
  status: ReportStatus;
  participantNote?: string;
  /** The sentence a review wrote for the participant, once one has decided. */
  reviewerNote?: string;
  source: "manual" | "screenshot" | "import";
  /**
   * The tracking-app ride this report was imported from, when it was one.
   *
   * Stands where a screenshot stands on a read ride: it is what an organizer
   * opens the report onto, and what lets a household see which ride in their
   * app a report came from.
   */
  importedRide?: IImportedRideOrigin;
  /**
   * The app the screenshot was recognised as, when one backed the ride.
   *
   * Not `source` above, which says whether the distance was typed or read.
   * Absent on a typed ride and on a screenshot the reader could not place.
   */
  trackerLabel?: string;
  createdAtMs: number;
  lastModifiedAtMs: number;
}

export { EvidenceDisposition, EvidenceJobStatus } from "./EvidenceJob";
export type { EvidenceConversion, EvidenceExtraction, EvidenceJob, EvidenceUploadResponse } from "./EvidenceJob";
export { EvidenceOutcomeKind } from "./EvidenceOutcomeKindEnum";
export type { EvidenceOutcome } from "./EvidenceOutcome";

/** An organised ride the co-hosts run. Informational: nothing to sign up for. */
export interface GroupRide {
  id: string;
  startsAtMs: number;
  meetingPoint: string;
  label: string;
}

export interface CampaignSnapshot {
  eventName: string;
  phase: CampaignPhase;
  /** True while the warm-up is under way, i.e. the campaign has not opened yet. */
  isWarmUp: boolean;
  /** Of the miles reported, how many were ridden before opening day. */
  warmUpMilesReported: number;
  /**
   * What those warm-up rides unlocked, in cents. Optional because a deployment
   * predating the field simply omits it, and the caption that states it is
   * omitted in turn rather than guessed at.
   */
  warmUpCents?: number;
  /** Miles up to which warm-up rides still count. Past this, rides must be dated in-campaign. */
  warmUpCarryOverMiles: number;
  startsOnMs: number;
  endsOnMs: number;
  milesReported: number;
  earningMiles: number;
  minorRideCount: number;
  mileageCents: number;
  minorRideCents: number;
  unlockedCents: number;
  finalContributionCents: number;
  capCents: number;
  floorCents: number;
  capReached: boolean;
  floorApplies: boolean;
  progress: number;
  countedReports: number;
  coHostSharesCents: number[];
  /** Organised rides to announce. Empty when the campaign runs none. */
  groupRides: GroupRide[];
}

/**
 * The three boards the campaign serves together.
 */
export interface LeaderboardData {
  households: LeaderboardHousehold[];
  adults: LeaderboardAdult[];
  minors: LeaderboardMinor[];
}

/**
 * One adult on the mileage board.
 */
export interface LeaderboardAdult {
  rank: number;
  displayName: string;
  miles: number;
}

/**
 * One minor on the rides board. Minors are counted in rides, never in distance.
 */
export interface LeaderboardMinor {
  rank: number;
  displayName: string;
  rideCount: number;
}

/**
 * Everything the campaign page renders: the snapshot behind the tracker and the
 * tally, the households behind the count, and the rides behind the feed.
 */
export interface CampaignOverview {
  snapshot: CampaignSnapshot | null;
  /** Null while the board has not been read, so an empty list is never mistaken for one. */
  households: LeaderboardHousehold[] | null;
  /** Null while the feed has not been read, so an empty feed is never mistaken for one. */
  latestRides: LatestRide[] | null;
  isLoading: boolean;
}

export interface AuthRequestResponse {
  message: string;
  /** The request this window is now waiting on, to name again when asking for a resend. */
  pendingSignInId: string;
  /** Absent when an existing request was reused — this window is already holding that one. */
  pendingSignInToken?: string;
  pendingSignInExpiresAtMs: number;
  /**
   * True when this request posted no mail because one went to the address
   * recently. Optional: a backend older than the field omits it, and a window
   * talking to one must not claim a send it cannot confirm.
   */
  wasSuppressedByCooldown?: boolean;
  /** When another mail may go to the address. Optional for the same reason. */
  nextLinkAllowedAtMs?: number;
}

/**
 * What creating a household answers with when the invitation already proved the
 * address, and the session was handed over instead of mailed.
 */
export interface HouseholdSignedInResponse {
  householdId: string;
  sessionToken: string;
  expiresAtMs: number;
}

/**
 * Creating a household answers one of two ways: signed in on the spot when an
 * invitation token came with it, or the usual wait for a mailed link.
 */
export type CreateHouseholdResponse = AuthRequestResponse | HouseholdSignedInResponse;

export interface HouseholdResponse {
  household: CharityHousehold;
  riders: CharityRider[];
  /** Every address that can sign in here, the head's included. */
  logins: IHouseholdLogin[];
  /** The session doing the asking, so the page shows only the actions it has. */
  viewer?: IHouseholdViewer;
}

/** The result of giving an adult a sign-in: the entry, and whether the note reached them. */
export interface HouseholdLoginGrantResponse {
  login: IHouseholdLogin;
  notified: string;
}

/** What an erasure removed. Reports are rewritten, never deleted. */
export interface HouseholdDeletionSummary {
  ridersDeleted: number;
  reportsRewritten: number;
  screenshotsDeleted: number;
  mileageDisposition: MileageDisposition;
}

export interface ReportsListResponse {
  reports: CharityRideReport[];
}

export interface SubmissionResponse {
  submissionGroupId: string;
  reports: CharityRideReport[];
}

export enum AdminSubmissionStatus {
  Pending = "pending",
  Counted = "counted",
  Rejected = "rejected",
  Duplicate = "duplicate",
  Withdrawn = "withdrawn",
}

export interface AdminRiderDetail {
  id: string;
  eventId: string;
  householdId: string;
  riderId: string;
  riderDisplayName: string;
  riderType: RiderType;
  miles: number;
  rideDateMs: number;
  submissionGroupId: string;
  status: AdminSubmissionStatus;
  supersedesReportId?: string;
  participantNote?: string;
  reviewerNote?: string;
  createdAtMs: number;
  lastModifiedAtMs: number;
}

export interface AdminSubmission {
  submissionGroupId: string;
  householdId: string;
  rideDateMs: number;
  status: AdminSubmissionStatus;
  createdAtMs: number;
  /**
   * Whether this submission's screenshot was also claimed by another household.
   *
   * Resolved by the service across the whole group, so the queue renders it
   * rather than working it out from `reports`. Organizer-facing only — the
   * household that filed the ride is told nothing, because a shared screenshot
   * is not a refusal and the ride counts either way.
   */
  hasSharedScreenshot: boolean;
  reports: AdminRiderDetail[];
}

export interface AdminQueueResponse {
  status: AdminSubmissionStatus;
  submissions: AdminSubmission[];
}

export interface AdminReportActionRequest {
  action: "approve" | "reject" | "mark_duplicate" | "annotate";
  reviewerNote?: string;
}

export interface AdminReportActionResponse {
  action: string;
  reportsUpdated: number;
}
export * from "./IRideAppInstruction";
